/**
 * Presentation metadata for factor states in the reporting surfaces.
 *
 * Colours come from the --state-* CSS custom properties (global.css), which the
 * admin Factor-state appearance editor can override at runtime via
 * GlobalStateStyles. Never hard-code state hex values here — always reference the
 * variable so reporting stays in sync with the rest of the app.
 *
 * Accessibility: pair every colour with the StateIcon glyph and a text label
 * (te reo + English) so state is never conveyed by colour alone (WCAG 1.4.1).
 */
import type { FactorStateKind } from "../framework/types";

export const ASSESSED_ORDER: readonly ("risk" | "change" | "protective")[] = [
  "risk",
  "change",
  "protective",
];

/** i18n key segment under framework.state.* for each kind. */
export const STATE_I18N_KEY: Record<FactorStateKind, string> = {
  risk: "risk",
  change: "change",
  protective: "protective",
  "not-assessed": "notAssessed",
};

/** CSS custom property holding the solid colour for each state. */
export function stateColourVar(kind: FactorStateKind): string {
  return `var(--state-${kind})`;
}

/** CSS custom property holding the soft background tint for each state. */
export function stateTintVar(kind: FactorStateKind): string {
  return `var(--state-${kind}-bg)`;
}

/** Translated te reo + English labels for a state. */
export function stateLabels(
  t: (key: string) => string,
  kind: FactorStateKind,
): { reo: string; en: string } {
  const seg = STATE_I18N_KEY[kind];
  return {
    reo: t(`framework.state.${seg}.teReo`),
    en: t(`framework.state.${seg}.descriptor`),
  };
}
